import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COUNTRY_CODES = [
  { country: "India", iso: "in", code: "+91" },
  { country: "United States", iso: "us", code: "+1" },
  { country: "United Kingdom", iso: "gb", code: "+44" },
  { country: "United Arab Emirates", iso: "ae", code: "+971" },
  { country: "Australia", iso: "au", code: "+61" },
  { country: "Bangladesh", iso: "bd", code: "+880" },
  { country: "Brazil", iso: "br", code: "+55" },
  { country: "Canada", iso: "ca", code: "+1" },
  { country: "China", iso: "cn", code: "+86" },
  { country: "France", iso: "fr", code: "+33" },
  { country: "Germany", iso: "de", code: "+49" },
  { country: "Indonesia", iso: "id", code: "+62" },
  { country: "Italy", iso: "it", code: "+39" },
  { country: "Japan", iso: "jp", code: "+81" },
  { country: "Malaysia", iso: "my", code: "+60" },
  { country: "Mexico", iso: "mx", code: "+52" },
  { country: "Nepal", iso: "np", code: "+977" },
  { country: "Netherlands", iso: "nl", code: "+31" },
  { country: "New Zealand", iso: "nz", code: "+64" },
  { country: "Nigeria", iso: "ng", code: "+234" },
  { country: "Pakistan", iso: "pk", code: "+92" },
  { country: "Philippines", iso: "ph", code: "+63" },
  { country: "Qatar", iso: "qa", code: "+974" },
  { country: "Russia", iso: "ru", code: "+7" },
  { country: "Saudi Arabia", iso: "sa", code: "+966" },
  { country: "Singapore", iso: "sg", code: "+65" },
  { country: "South Africa", iso: "za", code: "+27" },
  { country: "South Korea", iso: "kr", code: "+82" },
  { country: "Spain", iso: "es", code: "+34" },
  { country: "Sri Lanka", iso: "lk", code: "+94" },
  { country: "Sweden", iso: "se", code: "+46" },
  { country: "Switzerland", iso: "ch", code: "+41" },
  { country: "Thailand", iso: "th", code: "+66" },
  { country: "Turkey", iso: "tr", code: "+90" },
  { country: "Vietnam", iso: "vn", code: "+84" },
];

const STANDARDS = [
  { value: "10th", label: "10th" },
  { value: "12th", label: "12th" },
];
const BOARDS = [
  { value: "KSEEB", label: "KSEEB (Karnataka)" },
  { value: "MSBSHSE", label: "MSBSHSE (Maharashtra)" },
  { value: "CBSE", label: "CBSE" },
];
const MEDIUMS = ["Kannada", "English", "Marathi"];

function CountryFlag({ iso }) {
  return (
    <img
      src={`https://flagcdn.com/w40/${iso}.png`}
      srcSet={`https://flagcdn.com/w80/${iso}.png 2x`}
      width="20"
      height="15"
      alt=""
      aria-hidden="true"
      className="h-[15px] w-5 shrink-0 rounded-[2px] object-cover"
    />
  );
}

function GoogleLogo() {
  return (
    <svg aria-hidden="true" className="h-5 w-5 shrink-0" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.98-.9 6.63-2.43l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.86A6.01 6.01 0 0 1 6.08 12c0-.65.11-1.27.31-1.86V7.52H3.04A10 10 0 0 0 2 12c0 1.61.38 3.14 1.04 4.48l3.35-2.62Z" />
      <path fill="#EA4335" d="M12 6.01c1.47 0 2.79.51 3.82 1.49l2.88-2.88A9.65 9.65 0 0 0 12 2a10 10 0 0 0-8.96 5.52l3.35 2.62C7.18 7.77 9.39 6.01 12 6.01Z" />
    </svg>
  );
}

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, loginWithGoogle, register, verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: isLogin ? "" : new URLSearchParams(window.location.search).get("email") || "",
    password: "",
    phone: "",
    standard: "",
    board: "",
    medium: "",
  });
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [code, setCode] = useState("");

  const googleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
      toast.success("Welcome!");
      navigate("/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail
        ? formatApiErrorDetail(err.response.data.detail)
        : err.message || "Google sign-in failed. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && (!form.standard || !form.board || !form.medium)) {
      setError("Please select your standard, board, and medium.");
      return;
    }
    setLoading(true);
    try {
      const countryCode = COUNTRY_CODES.find(({ country }) => country === selectedCountry)?.code || "+91";
      const result = isLogin
        ? await login(form.email, form.password)
        : await register({
            name: form.name,
            email: form.email,
            password: form.password,
            phone: form.phone ? `${countryCode}${form.phone}` : "",
            standard: form.standard,
            board: form.board,
            medium: form.medium,
          });
      if (result?.verification_required) {
        setVerificationEmail(result.email);
        toast.success("Verification code sent to your email");
        return;
      }
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitVerification = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyEmail(verificationEmail, code);
      toast.success("Email verified!");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    setLoading(true);
    try {
      await resendVerification(verificationEmail);
      toast.success("A new verification code was sent");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (verificationEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submitVerification} className="w-full max-w-sm space-y-5">
          <div>
            <p className="overline text-primary">Verify email</p>
            <h1 className="font-heading font-black text-3xl tracking-tight mt-2">Check your inbox</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Enter the 6-digit code sent to <span className="font-medium text-foreground">{verificationEmail}</span>.
              It expires in 10 minutes.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="verification-code">Verification code</Label>
            <Input id="verification-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              pattern="[0-9]{6}" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="auth-verification-code" required autoFocus />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
          <Button type="submit" className="w-full rounded-full" disabled={loading || code.length !== 6}
            data-testid="auth-verify-submit">{loading ? "Please wait…" : "Verify email"}</Button>
          <Button type="button" variant="ghost" className="w-full" disabled={loading} onClick={resend}
            data-testid="auth-resend-code">Resend code</Button>
          <button type="button" className="w-full text-sm text-muted-foreground" onClick={() => { setVerificationEmail(""); setCode(""); setError(""); }}>
            Use a different email
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <Link to="/" className="font-heading font-black text-2xl tracking-tight">ThetaExams</Link>
        <div>
          <h2 className="font-heading font-black text-4xl tracking-tight leading-tight">
            Every mark, explained.
          </h2>
          <p className="text-primary-foreground/80 mt-4 max-w-md leading-relaxed">
            AI-graded handwritten answer scripts in any Indian language, with per-question feedback and guidance.
          </p>
        </div>
        <p className="text-primary-foreground/60 text-sm">AI answer-script evaluation for students</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <p className="overline text-primary">{isLogin ? "Sign in" : "Create account"}</p>
            <h1 className="font-heading font-black text-3xl tracking-tight mt-2">
              {isLogin ? "Welcome back" : "Start evaluating"}
            </h1>
          </div>

          <Button type="button" variant="outline" className="w-full rounded-full gap-2" disabled={loading} onClick={googleSignIn} data-testid="auth-google">
            <GoogleLogo />
            <span>{loading ? "Please wait…" : "Continue with Google"}</span>
          </Button>
          <p className="text-center text-sm text-muted-foreground">or continue with email</p>

          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="auth-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
          )}
          {!isLogin && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="board">Board</Label>
                <Select value={form.board} onValueChange={(board) => setForm({ ...form, board })} required>
                  <SelectTrigger id="board" data-testid="auth-board" aria-label="Board">
                    <SelectValue placeholder="Select board" />
                  </SelectTrigger>
                  <SelectContent>
                    {BOARDS.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="standard">Standard</Label>
                <Select value={form.standard} onValueChange={(standard) => setForm({ ...form, standard })} required>
                  <SelectTrigger id="standard" data-testid="auth-standard" aria-label="Standard">
                    <SelectValue placeholder="Select standard" />
                  </SelectTrigger>
                  <SelectContent>
                    {STANDARDS.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="medium">Medium</Label>
                <Select value={form.medium} onValueChange={(medium) => setForm({ ...form, medium })} required>
                  <SelectTrigger id="medium" data-testid="auth-medium" aria-label="Medium">
                    <SelectValue placeholder="Select medium" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIUMS.map((medium) => (
                      <SelectItem key={medium} value={medium}>{medium}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="auth-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </div>
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="phone">Phone number</Label>
              <div className="flex gap-2">
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger
                    className="w-[118px] shrink-0"
                    aria-label="Country code"
                    data-testid="auth-country-code"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRY_CODES.map(({ country, iso, code }) => (
                      <SelectItem key={country} value={country}>
                        <span className="flex items-center gap-2">
                          <CountryFlag iso={iso} country={country} />
                          <span>{code}</span>
                          {/* <span className="text-muted-foreground">{country}</span> */}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  data-testid="auth-phone"
                  placeholder="98765 43210"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  minLength={10}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  aria-describedby="phone-help"
                  aria-invalid={form.phone.length > 0 && form.phone.length !== 10}
                  className="min-w-0"
                  required
                />
              </div>
              {/* <p id="phone-help" className={`text-xs ${form.phone && form.phone.length !== 10 ? "text-destructive" : "text-muted-foreground"}`}>
                Enter exactly 10 digits.
              </p> */}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" data-testid="auth-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          </div>

          {isLogin && <Link to="/forgot-password" className="block text-sm text-primary text-right" data-testid="login-forgot-password-link">Forgot password?</Link>}
          {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

          <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="auth-submit">
            {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            {isLogin ? (
              <>New here? <Link to="/register" className="text-primary font-medium">Create an account</Link></>
            ) : (
              <>Have an account? <Link to="/login" className="text-primary font-medium">Sign in</Link></>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
